/**
 * Partner fields backed by a mounted CarrierWave uploader on the server
 * (`mount_uploader` in civicdinners_web's app/models/partner.rb).
 *
 * These are camelCase here and get snakified on the way out, so `backgroundImage`
 * is submitted as `background_image`. The `remove_<field>` flags are separate
 * params and are deliberately NOT in this list -- they are booleans, they are
 * how a deletion is requested, and they must keep being sent.
 */
export const PARTNER_IMAGE_FIELDS = ['profileImage', 'backgroundImage', 'logoImage', 'privateLabelEmailHeaderImage', 'metaImage']

/**
 * Drops image values that are not an actual upload.
 *
 * The partner form submits every field on every save, and `FileButtonControl`
 * gives an untouched image field its stored image's URL as the default value
 * (`defaultValue?.url || defaultValue || ''`). For an S3-backed image that is a
 * presigned URL string.
 *
 * Sending that string back is destructive. Under carrierwave 3.x, a String
 * assigned to a mounted uploader is read as a cache name; the lookup fails, the
 * uploader is dropped, `write_identifier` then writes nil to the column, and an
 * `after_commit` hook deletes the stored file and all of its versions. So a save
 * that changed only the partner's name was deleting the partner's branding.
 *
 * At submit time each image field holds exactly one of two things -- a URL
 * `String` (untouched, or an empty string when unset) or a `File` (freshly
 * uploaded) -- so `instanceof File` is an unambiguous test. `null` (the value
 * `FileButtonControl` sets when Delete is clicked) is dropped too; the removal
 * travels on the `remove_<field>` flag, not on this one.
 *
 * This is the second line of defence. The authoritative guard is server-side in
 * Api::PartnersController, which is where the deletion actually happens and
 * which protects every caller rather than just this form.
 */
export const withoutNonFileImages = <T extends Record<string, any>>(data: T): Partial<T> => {
  const filtered: Record<string, any> = { ...data }

  PARTNER_IMAGE_FIELDS.forEach((field) => {
    if (field in filtered && !(filtered[field] instanceof File)) {
      delete filtered[field]
    }
  })

  return filtered as Partial<T>
}
